import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExpandMore,
  ContentCopy,
  Save,
  Publish,
  OpenWith,
  Refresh,
  Archive,
  FileDownload,
  History,
} from "@mui/icons-material";
import AdminLayout from "../../layouts/AdminLayout";
import BoxPositionnerEditor from "../../components/admin/BoxPositionnerEditor";
import MineCartObstaclesEditor from "../../components/admin/MineCartObstaclesEditor";
import ScenarioLayoutPreview from "../../components/admin/ScenarioLayoutPreview";
import {
  getAdminScenarios,
  createScenarioVariant,
  updateScenarioVariant,
  publishScenarioVariant,
  uploadScenarioImage,
  getScenarioVariantHistory,
  restoreScenarioVariant,
  exportScenarioVariant,
  getScenarioVariantPackage,
} from "../../services/adminService";
import { imageUrl, resolvePreview } from "../../utils/scenarioVariants";
import { unwrapBoxes } from "../../utils/adventureLayoutFormat";
const NAMES = {
  combat_monstre: "Combat",
  chemin_foret: "Chemin",
  course: "Course au trésor",
  crochetage: "Crochetage",
  infiltration: "Infiltration",
  defense_zone: "Défense",
  examen_sage: "Examen du sage",
  fabrication: "Fabrication",
  wagonnet_fou: "Wagonnet",
  duel: "Duel",
  carrefour: "Carrefour",
  avalanche: "Avalanche",
  pont_paires: "Pont des paires",
  fuite: "Fuite",
  course_poursuite: "Course poursuite",
};
const EMPTY = { scenarios: [], variants: [], usages: [] };
const slug = (s) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .slice(0, 30);
const stringify = (x) => JSON.stringify(x);
const LIVRAISON = {
  conforme: { label: "Livrée par le dépôt", color: "success" },
  retouchee: { label: "Retouchée depuis la livraison", color: "warning" },
};
const ORIGINES = { admin: "publiée ici", livraison: "livrée par le dépôt" };
export default function ScenariosScreen() {
  const [catalog, setCatalog] = useState(EMPTY),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const [scenarioId, setScenarioId] = useState(""),
    [selection, setSelection] = useState(""),
    [draft, setDraft] = useState(null),
    [saved, setSaved] = useState("");
  const [newDialog, setNewDialog] = useState(false),
    [newName, setNewName] = useState(""),
    [publishDialog, setPublishDialog] = useState(false),
    [layoutOpen, setLayoutOpen] = useState(false),
    [pendingSelection, setPendingSelection] = useState(null),
    [history, setHistory] = useState([]);
  const dirty = !!draft && stringify(draft) !== saved;
  const scenario = catalog.scenarios.find((s) => s.id === scenarioId),
    custom = catalog.variants.find((v) => v.id === selection);
  const baseId = custom?.baseThemeId || selection,
    base = scenario?.themes[baseId],
    fields = scenario?.fields[baseId];
  const preview = useMemo(
    () => (base ? resolvePreview(base, draft?.definition) : null),
    [base, draft],
  );
  useEffect(() => {
    setHistory([]);
    if (!custom?.id) return;
    getScenarioVariantHistory(custom.id)
      .then(setHistory)
      .catch(() => {});
  }, [custom?.id, custom?.revision]);
  const usages = catalog.usages.filter(
    (u) => u.typeScenario === scenarioId && u.themeVisuel === selection,
  );
  const select = useCallback((data, sid, id) => {
    setScenarioId(sid);
    setSelection(id);
    const row = data.variants.find((v) => v.id === id);
    const value = row
      ? { label: row.label, definition: structuredClone(row.draft) }
      : null;
    setDraft(value);
    setSaved(value ? stringify(value) : "");
    setError("");
    setNotice("");
  }, []);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminScenarios();
      setCatalog(data);
      const sid = scenarioId || data.scenarios[0]?.id;
      const id =
        selection || data.scenarios.find((s) => s.id === sid)?.defaultTheme;
      select(data, sid, id);
    } catch (e) {
      setError(e.message || "Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [scenarioId, selection, select]);
  useEffect(() => {
    reload();
  }, []);
  useEffect(() => {
    const f = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", f);
    return () => window.removeEventListener("beforeunload", f);
  }, [dirty]);
  const choose = (sid, id) => {
    if (dirty) setPendingSelection({ sid, id });
    else select(catalog, sid, id);
  };
  const change = (group, key, value) =>
    setDraft((d) => ({
      ...d,
      definition: {
        ...d.definition,
        [group]: { ...d.definition[group], [key]: value },
      },
    }));
  const reset = (group, key) =>
    setDraft((d) => {
      const def = structuredClone(d.definition);
      delete def[group]?.[key];
      return { ...d, definition: def };
    });
  const commitRow = (row) => {
    setCatalog((c) => ({
      ...c,
      variants: [...c.variants.filter((v) => v.id !== row.id), row],
    }));
    const d = { label: row.label, definition: structuredClone(row.draft) };
    setDraft(d);
    setSaved(stringify(d));
    return row;
  };
  const action = async (fn) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
    } catch (e) {
      setError(e.message || "Action impossible");
    } finally {
      setBusy(false);
    }
  };
  const save = async () =>
    commitRow(
      await updateScenarioVariant(custom.id, {
        ...draft,
        revision: custom.revision,
      }),
    );
  const create = () =>
    action(async () => {
      const name = newName.trim();
      if (!name) throw Error("Donnez un nom à la variante");
      const row = await createScenarioVariant({
        id: `v_${slug(name)}_${crypto.randomUUID().slice(0, 8)}`,
        typeScenario: scenarioId,
        baseThemeId: baseId,
        label: name,
        definition: draft?.definition || {},
      });
      const data = { ...catalog, variants: [...catalog.variants, row] };
      setCatalog(data);
      select(data, scenarioId, row.id);
      setNewDialog(false);
      setNotice("Variante créée en brouillon.");
    });
  const exportToRepo = () =>
    action(async () => {
      const r = await exportScenarioVariant(custom.id);
      await reload();
      setNotice(
        `Version publiée écrite dans ${r.fichier} (${r.images} image(s)). Vérifiez le diff, puis commitez : le prochain déploiement la livrera.`,
      );
    });
  const downloadPackage = () =>
    action(async () => {
      const contenu = await getScenarioVariantPackage(custom.id);
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(contenu)], { type: "application/json" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `${custom.id}.paquet.json`;
      a.click();
      URL.revokeObjectURL(url);
      setNotice(
        "Paquet téléchargé. Sur le poste : node backend/scripts/livrer_variantes_scenarios.js --deposer <fichier>, puis commit.",
      );
    });
  const restore = (publication) =>
    action(async () => {
      commitRow(
        await restoreScenarioVariant(
          custom.id,
          publication.id,
          custom.revision,
        ),
      );
      setNotice(
        `Version ${publication.revision} reprise en brouillon : relisez-la, puis publiez-la.`,
      );
    });
  const upload = (key, file) =>
    action(async () => {
      if (!file) return;
      const image = await uploadScenarioImage(file);
      change("assets", key, image);
      setNotice("Image ajoutée au brouillon ; sauvegardez pour la conserver.");
    });
  return (
    <AdminLayout>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4">Scénarios et variantes</Typography>
            <Typography color="text.secondary">
              Un catalogue partagé par toutes les histoires.
            </Typography>
          </Box>
          <Button
            startIcon={<Refresh />}
            disabled={busy || dirty || loading}
            onClick={reload}
          >
            Actualiser
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                fullWidth
                select
                label="Scénario"
                value={scenarioId}
                disabled={busy}
                onChange={(e) => {
                  const s = catalog.scenarios.find(
                    (s) => s.id === e.target.value,
                  );
                  choose(s.id, s.defaultTheme);
                }}
              >
                {catalog.scenarios.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {NAMES[s.id] || s.id} ·{" "}
                    {Object.keys(s.themes).length +
                      catalog.variants.filter((v) => v.typeScenario === s.id)
                        .length}{" "}
                    variantes
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                label="Variante"
                value={selection}
                disabled={busy}
                onChange={(e) => choose(scenarioId, e.target.value)}
              >
                {Object.keys(scenario?.themes || {}).map((id) => (
                  <MenuItem key={id} value={id}>
                    {id} · modèle embarqué
                  </MenuItem>
                ))}
                {catalog.variants
                  .filter((v) => v.typeScenario === scenarioId)
                  .map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.label} · {v.published ? "publiée" : "brouillon"}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
            {preview && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    lg: "minmax(0, 1fr) minmax(0, 1fr)",
                  },
                  gap: 2,
                }}
              >
                <Stack spacing={2}>
                  <Card>
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={
                              custom ? "Variante partagée" : "Modèle embarqué"
                            }
                            color={custom ? "primary" : "default"}
                          />
                          {custom && (
                            <Chip
                              label={custom.published ? "Publiée" : "Brouillon"}
                              color={custom.published ? "success" : "warning"}
                            />
                          )}
                          {LIVRAISON[custom?.etatLivraison] && (
                            <Chip
                              variant="outlined"
                              label={LIVRAISON[custom.etatLivraison].label}
                              color={LIVRAISON[custom.etatLivraison].color}
                            />
                          )}
                          {dirty && (
                            <Chip
                              label="Modifications non sauvegardées"
                              color="warning"
                            />
                          )}
                        </Stack>
                        {custom ? (
                          <TextField
                            label="Nom de la variante"
                            value={draft?.label || ""}
                            disabled={busy}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, label: e.target.value }))
                            }
                          />
                        ) : (
                          <Alert severity="info">
                            Ce modèle décrit le renderer existant. Dupliquez-le
                            pour créer une variante éditable.
                          </Alert>
                        )}
                        <Typography variant="caption">
                          Identifiant : {selection} · modèle : {baseId}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            startIcon={<ContentCopy />}
                            disabled={busy || dirty}
                            onClick={() => {
                              setNewName(
                                custom
                                  ? `${custom.label} — copie`
                                  : `${NAMES[scenarioId]} — nouvelle variante`,
                              );
                              setNewDialog(true);
                            }}
                          >
                            Dupliquer
                          </Button>
                          {custom && (
                            <>
                              <Button
                                startIcon={<Save />}
                                disabled={busy || !dirty}
                                onClick={() =>
                                  action(async () => {
                                    await save();
                                    setNotice("Brouillon sauvegardé.");
                                  })
                                }
                              >
                                Sauvegarder
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<Publish />}
                                disabled={busy || dirty}
                                onClick={() => setPublishDialog(true)}
                              >
                                Publier
                              </Button>
                            </>
                          )}
                          {custom?.published && catalog.exportVersDepot && (
                            <Button
                              startIcon={<Archive />}
                              disabled={busy || dirty}
                              onClick={exportToRepo}
                            >
                              Exporter vers le dépôt
                            </Button>
                          )}
                          {custom?.published && (
                            <Button
                              startIcon={<FileDownload />}
                              disabled={busy}
                              onClick={downloadPackage}
                            >
                              Télécharger le paquet
                            </Button>
                          )}
                        </Stack>
                        {custom && (
                          <Typography variant="body2" color="text.secondary">
                            Sauvegarder conserve le brouillon. Publier rend cet
                            habillage disponible pour les niveaux qui utilisent
                            son identifiant.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Stack spacing={2}>
                        <Typography variant="h6">
                          Prévisualisation du layout
                        </Typography>
                        <ScenarioLayoutPreview
                          scenarioId={scenarioId}
                          config={preview}
                        />
                        <Button
                          startIcon={<OpenWith />}
                          onClick={() => setLayoutOpen(true)}
                          disabled={!custom || !scenario.layoutEditable || busy}
                        >
                          Modifier le placement
                        </Button>
                        {scenarioId === "wagonnet_fou" && (
                          <Typography variant="caption" color="text.secondary">
                            Sol : ligne de contact à 42 % de sa hauteur (largeur
                            de scène). Véhicule : cadre du corps ; les appuis et
                            le compagnon le suivent. Obstacle : cadre à
                            l’entrée, déplacé ensuite par le renderer. Sortie :
                            zone de lumière à la victoire. Déplacez ensemble les
                            repères pour conserver leur contact au sol.
                          </Typography>
                        )}
                        {scenarioId === "avalanche" && (
                          <Typography variant="caption" color="text.secondary">
                            pied_pente et sommet_pente : extrémités du chemin.
                            compagnon : taille (carrée). projectile : taille du
                            gros bloc. depart_rochers : origine de la chute. but
                            : refuge. Le calque salve suit automatiquement
                            scene.
                          </Typography>
                        )}
                        {scenarioId === "course_poursuite" && (
                          <Typography variant="caption" color="text.secondary">
                            player_track / rival_track : départs et lignes de
                            pieds. player_sprite / rival_sprite : tailles
                            carrées, réglées par la hauteur. player_end /
                            rival_end : points d’arrivée (bord droit et pieds).
                            Ajustez ces points avec race_finish pour les aligner
                            sur le coffre.
                          </Typography>
                        )}
                        {!scenario.layoutEditable && (
                          <Typography variant="caption">
                            Ce renderer possède un placement fixe : son layout
                            est affiché pour référence.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">
                        Utilisations · {usages.length} niveau(s)
                      </Typography>
                      {usages.length ? (
                        usages.map((u) => (
                          <Typography key={u.id} variant="body2" sx={{ mt: 1 }}>
                            {u.storyTitle} → {u.nom} ({u.id})
                          </Typography>
                        ))
                      ) : (
                        <Typography color="text.secondary">
                          Aucun niveau ne référence cette variante.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                  {custom && (
                    <Card>
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <History fontSize="small" />
                          <Typography variant="h6">
                            Historique des publications
                          </Typography>
                        </Stack>
                        {history.length ? (
                          history.map((h, i) => (
                            <Stack
                              key={h.id}
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ mt: 1 }}
                            >
                              <Typography variant="body2">
                                {new Date(h.publieLe).toLocaleString("fr-FR")} ·{" "}
                                {ORIGINES[h.origine] || h.origine} ·{" "}
                                {h.published?.label}
                                {i === 0 ? " (servie au jeu)" : ""}
                              </Typography>
                              {i > 0 && (
                                <Button
                                  size="small"
                                  disabled={busy || dirty}
                                  onClick={() => restore(h)}
                                >
                                  Reprendre comme brouillon
                                </Button>
                              )}
                            </Stack>
                          ))
                        ) : (
                          <Typography color="text.secondary" sx={{ mt: 1 }}>
                            Aucune publication enregistrée.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Stack>
                <Stack spacing={1}>
                  {["wagonnet_fou", "avalanche"].includes(scenarioId) && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Obstacles : hauteur et variété</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <MineCartObstaclesEditor
                          base={base}
                          config={preview}
                          value={draft?.definition.obstacleIllustrations}
                          disabled={!custom || busy}
                          onChange={(rows) =>
                            setDraft((d) => {
                              const definition = { ...d.definition };
                              if (rows) definition.obstacleIllustrations = rows;
                              else delete definition.obstacleIllustrations;
                              return { ...d, definition };
                            })
                          }
                          onUpload={(file, callback) =>
                            action(async () => {
                              callback(await uploadScenarioImage(file));
                              setNotice(
                                "Obstacle ajouté au brouillon ; sauvegardez pour le conserver.",
                              );
                            })
                          }
                        />
                      </AccordionDetails>
                    </Accordion>
                  )}
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>
                        Images et états ·{" "}
                        {Object.keys(fields?.assets || {}).length}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {Object.entries(fields?.assets || {}).map(
                          ([key, original]) => {
                            const image =
                              draft?.definition.assets?.[key] || original;
                            return (
                              <Stack
                                key={key}
                                direction="row"
                                alignItems="center"
                                spacing={2}
                              >
                                <Box
                                  component="img"
                                  src={imageUrl(image)}
                                  alt={key}
                                  sx={{
                                    width: 80,
                                    height: 65,
                                    objectFit: "contain",
                                    bgcolor: "#eee",
                                  }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ overflowWrap: "anywhere" }}
                                  >
                                    {key}
                                  </Typography>
                                  {image.width && (
                                    <Typography variant="caption">
                                      {image.width} × {image.height}
                                    </Typography>
                                  )}
                                  {custom && (
                                    <Stack direction="row">
                                      <Button component="label" disabled={busy}>
                                        Remplacer
                                        <input
                                          hidden
                                          type="file"
                                          accept="image/png,image/jpeg,image/webp"
                                          onChange={(e) => {
                                            upload(key, e.target.files?.[0]);
                                            e.target.value = "";
                                          }}
                                        />
                                      </Button>
                                      <Button
                                        disabled={
                                          busy ||
                                          !draft?.definition.assets?.[key]
                                        }
                                        onClick={() => reset("assets", key)}
                                      >
                                        Réinitialiser
                                      </Button>
                                    </Stack>
                                  )}
                                </Box>
                              </Stack>
                            );
                          },
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  {["colors", "strings"].map((group) => (
                    <Accordion key={group}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>
                          {group === "colors"
                            ? "Palette"
                            : "Textes de la scène"}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          {Object.entries(fields?.[group] || {}).map(
                            ([key, value]) => (
                              <Stack key={key} direction="row" spacing={1}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={key}
                                  value={
                                    draft?.definition[group]?.[key] ?? value
                                  }
                                  disabled={!custom || busy}
                                  onChange={(e) =>
                                    change(group, key, e.target.value)
                                  }
                                />
                                {custom && (
                                  <Button
                                    disabled={
                                      busy ||
                                      draft?.definition[group]?.[key] ===
                                        undefined
                                    }
                                    onClick={() => reset(group, key)}
                                  >
                                    Réinitialiser
                                  </Button>
                                )}
                              </Stack>
                            ),
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                  {custom && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Briefing et résultat</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          {[
                            "descriptionObjective",
                            "titreVictoire",
                            "titreDefaite",
                          ].map((key) => (
                            <TextField
                              key={key}
                              fullWidth
                              label={
                                {
                                  descriptionObjective: "Objectif",
                                  titreVictoire: "Titre de victoire",
                                  titreDefaite: "Titre de défaite",
                                }[key]
                              }
                              value={draft.definition.meta?.[key] || ""}
                              disabled={busy}
                              onChange={(e) =>
                                e.target.value
                                  ? change("meta", key, e.target.value)
                                  : reset("meta", key)
                              }
                            />
                          ))}
                          {["reglesJeu", "reglesJeuTempsLibre"].map((key) => (
                            <TextField
                              key={key}
                              fullWidth
                              multiline
                              minRows={3}
                              label={
                                key === "reglesJeu"
                                  ? "Règles affichées (une par ligne)"
                                  : "Règles affichées en temps libre (une par ligne)"
                              }
                              helperText="Laisser vide conserve les textes du modèle. Les règles de jeu restent inchangées."
                              value={(draft.definition.meta?.[key] || []).join(
                                "\n",
                              )}
                              disabled={busy}
                              onChange={(e) =>
                                e.target.value
                                  ? change(
                                      "meta",
                                      key,
                                      e.target.value.split("\n"),
                                    )
                                  : reset("meta", key)
                              }
                            />
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Stack>
      <Dialog open={newDialog} onClose={() => !busy && setNewDialog(false)}>
        <DialogTitle>Nouvelle variante partagée</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nom"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Le modèle et les images sont conservés. La variante pourra être
            utilisée dans plusieurs histoires.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setNewDialog(false)}>
            Annuler
          </Button>
          <Button disabled={busy || !newName.trim()} onClick={create}>
            Créer le brouillon
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={publishDialog}
        onClose={() => !busy && setPublishDialog(false)}
      >
        <DialogTitle>Publier {draft?.label} ?</DialogTitle>
        <DialogContent>
          <Typography>
            Cette publication sera utilisée par {usages.length} niveau(s). Les
            progressions et les règles de jeu restent inchangées.
          </Typography>
          {usages.map((u) => (
            <Typography key={u.id} variant="body2">
              {u.storyTitle} → {u.nom}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPublishDialog(false)}>
            Annuler
          </Button>
          <Button
            disabled={busy}
            onClick={() =>
              action(async () => {
                commitRow(
                  await publishScenarioVariant(custom.id, custom.revision),
                );
                setPublishDialog(false);
                setNotice(
                  "Variante publiée. Elle est disponible dans l’éditeur des niveaux.",
                );
              })
            }
          >
            Publier cette version
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!pendingSelection}
        onClose={() => setPendingSelection(null)}
      >
        <DialogTitle>Abandonner les modifications du brouillon ?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setPendingSelection(null)}>
            Continuer à éditer
          </Button>
          <Button
            onClick={() => {
              select(catalog, pendingSelection.sid, pendingSelection.id);
              setPendingSelection(null);
            }}
          >
            Abandonner
          </Button>
        </DialogActions>
      </Dialog>
      {preview && (
        <BoxPositionnerEditor
          open={layoutOpen}
          onClose={() => setLayoutOpen(false)}
          title={`Placement — ${draft?.label || selection}`}
          layout={preview.layout}
          backgroundImageUrl={imageUrl(preview.background)}
          onSaveDraft={(boxes) => {
            setDraft((d) => ({
              ...d,
              definition: {
                ...d.definition,
                layout: Array.isArray(base.layout)
                  ? boxes
                  : { ...base.layout, boxes },
              },
            }));
            setLayoutOpen(false);
          }}
        />
      )}
    </AdminLayout>
  );
}
